/**
 * Persist Google Visibility item status/notes per organization.
 *
 * Catalog lives in code. This table only stores org-scoped state so future
 * Search Console / GBP adapters can add evidence without a V1 API integration.
 */

import { supabase } from "../supabase";
import { ITEM_BY_ID, normalizeItemStatus } from "./catalog";
import {
  MAX_ITEM_NOTE_LENGTH,
  mergeCatalogWithStates,
  sanitizeItemNote,
} from "./itemState";
import {
  applyOrganizationScope,
  resolveOrganizationIdForQueries,
  stampOrganizationId,
} from "../helpers/orgQueryScope";
import {
  SURFACE_RENAUD_ORGANIZATION_ID,
  actorCanAccessGoogleVisibility,
} from "../helpers/googleVisibilityAccess";

export const GOOGLE_VISIBILITY_TABLE = "google_visibility_item_states";
export const MAX_NOTE_LENGTH = MAX_ITEM_NOTE_LENGTH;

export { mergeCatalogWithStates };

export const PERSIST_ERROR = Object.freeze({
  MISSING_TABLE: "missing_table",
  ACCESS: "access",
  UNAVAILABLE: "unavailable",
  SAVE: "save",
  LOAD: "load",
});

const USER_FACING_MESSAGES = Object.freeze({
  [PERSIST_ERROR.MISSING_TABLE]:
    "Status tracking is not available on this environment yet.",
  [PERSIST_ERROR.ACCESS]: "You do not have access to save Google Visibility status.",
  [PERSIST_ERROR.UNAVAILABLE]: "Status tracking is temporarily unavailable.",
  [PERSIST_ERROR.SAVE]: "Could not save status. Try again.",
  [PERSIST_ERROR.LOAD]: "Could not load saved status. Showing the checklist only.",
});

export function classifyPersistError(error) {
  const message = String(error?.message || "");
  const code = String(error?.code || "");
  if (
    code === "PGRST205" ||
    code === "42P01" ||
    /schema cache/i.test(message) ||
    /could not find the table/i.test(message) ||
    /relation .* does not exist/i.test(message)
  ) {
    return PERSIST_ERROR.MISSING_TABLE;
  }
  if (code === "42501" || /permission denied|row-level security/i.test(message)) {
    return PERSIST_ERROR.ACCESS;
  }
  return PERSIST_ERROR.UNAVAILABLE;
}

export function userFacingPersistMessage(kind) {
  return USER_FACING_MESSAGES[kind] || USER_FACING_MESSAGES[PERSIST_ERROR.UNAVAILABLE];
}

export function sanitizeNote(note) {
  return sanitizeItemNote(note);
}

async function resolveScopedOrgId(actor, client) {
  if (!actorCanAccessGoogleVisibility(actor)) {
    return null;
  }

  const organizationId = await resolveOrganizationIdForQueries(actor, client);
  if (!organizationId || organizationId !== SURFACE_RENAUD_ORGANIZATION_ID) {
    return null;
  }
  return organizationId;
}

export async function loadGoogleVisibilityStates(actor, client = supabase) {
  const organizationId = await resolveScopedOrgId(actor, client);
  if (!organizationId) {
    return {
      states: mergeCatalogWithStates([]),
      persistAvailable: false,
      errorKind: PERSIST_ERROR.ACCESS,
      error: userFacingPersistMessage(PERSIST_ERROR.ACCESS),
    };
  }

  if (!client?.from) {
    return {
      states: mergeCatalogWithStates([]),
      persistAvailable: false,
      errorKind: PERSIST_ERROR.UNAVAILABLE,
      error: userFacingPersistMessage(PERSIST_ERROR.UNAVAILABLE),
    };
  }

  let query = client
    .from(GOOGLE_VISIBILITY_TABLE)
    .select(
      "item_key, status, note, evidence, updated_at, updated_by_profile_id, organization_id",
    );
  query = applyOrganizationScope(query, organizationId);

  const { data, error } = await query;

  if (error) {
    const errorKind = classifyPersistError(error);
    return {
      states: mergeCatalogWithStates([]),
      persistAvailable: false,
      errorKind,
      error: userFacingPersistMessage(
        errorKind === PERSIST_ERROR.MISSING_TABLE
          ? PERSIST_ERROR.MISSING_TABLE
          : PERSIST_ERROR.LOAD,
      ),
    };
  }

  return {
    states: mergeCatalogWithStates(data || []),
    persistAvailable: true,
    errorKind: null,
    error: null,
  };
}

export async function saveGoogleVisibilityItemState(
  actor,
  { itemId, status, note },
  client = supabase,
) {
  const organizationId = await resolveScopedOrgId(actor, client);
  if (!organizationId) {
    return {
      data: null,
      error: { message: userFacingPersistMessage(PERSIST_ERROR.ACCESS) },
      errorKind: PERSIST_ERROR.ACCESS,
    };
  }

  if (!ITEM_BY_ID[itemId]) {
    return {
      data: null,
      error: { message: userFacingPersistMessage(PERSIST_ERROR.SAVE) },
      errorKind: PERSIST_ERROR.SAVE,
    };
  }

  const payload = stampOrganizationId(
    {
      organization_id: organizationId,
      item_key: itemId,
      status: normalizeItemStatus(status),
      note: sanitizeNote(note),
      updated_by_profile_id: actor.profile_id || null,
      updated_at: new Date().toISOString(),
    },
    organizationId,
  );

  const { data, error } = await client
    .from(GOOGLE_VISIBILITY_TABLE)
    .upsert(payload, { onConflict: "organization_id,item_key" })
    .select(
      "item_key, status, note, evidence, updated_at, updated_by_profile_id, organization_id",
    )
    .single();

  if (error) {
    const errorKind = classifyPersistError(error);
    return {
      data: null,
      error: { message: userFacingPersistMessage(PERSIST_ERROR.SAVE) },
      errorKind,
    };
  }

  return { data, error: null, errorKind: null };
}
